import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

// Presentationsfilm för byggföretag (juli 2026). Tomt = inget videoblock visas.
const WELCOME_VIDEO_ID = '0eBxEarfdJc';

export interface OnboardingSteps {
  customerInvited: boolean;
  kycApproved: boolean;
  projectCreated: boolean;
  practicalInfoComplete: boolean;
}

interface CompanyOnboardingGuideProps {
  companyName?: string;
  steps: OnboardingSteps;
  showKycStep?: boolean;
  collapsed: boolean;
  animate: boolean;
  onToggleCollapsed: (collapsed: boolean) => void;
  onInviteCustomer: () => void;
  onStartKyc: () => void;
  isStartingKyc: boolean;
  /** When present (and non-mock), the KYC step links out instead of calling onStartKyc. */
  kycOnboardingUrl?: string | null;
  kycMessage?: { type: 'success' | 'error'; text: string } | null;
}

type StepKey = keyof OnboardingSteps;

export function CompanyOnboardingGuide({
  companyName,
  steps,
  showKycStep = true,
  collapsed,
  animate,
  onToggleCollapsed,
  onInviteCustomer,
  onStartKyc,
  isStartingKyc,
  kycOnboardingUrl,
  kycMessage
}: CompanyOnboardingGuideProps) {
  const { t, i18n } = useTranslation();
  const [videoPlaying, setVideoPlaying] = useState(false);
  const isEnglish = (i18n.language || 'sv').toLowerCase().startsWith('en');
  const videoCaption = t(
    'companyOnboardingGuide.videoCaption',
    'Vi har använt AI i framtagandet av detta material'
  );
  // Part 2 is collapsed by default once a project exists (the flow it describes
  // has then been reached inside the project).
  const [part2Open, setPart2Open] = useState(!steps.projectCreated);
  // Lets the user reopen the full guide after it auto-shrinks to the help box.
  const [forceOpen, setForceOpen] = useState(false);

  const order: StepKey[] = [
    'customerInvited',
    ...(showKycStep ? ['kycApproved' as const] : []),
    'projectCreated',
    'practicalInfoComplete'
  ];
  const doneCount = order.filter((key) => steps[key]).length;
  const allDone = doneCount === order.length;
  const nextIncomplete = order.find((key) => !steps[key]) ?? null;

  const name = companyName?.trim() || t('home.companyGuide.fallbackName', 'företaget');

  // All applicable steps done → compact help box (reopenable).
  if (allDone && !forceOpen) {
    return (
      <div className="card mb-4">
        <div className="card-body d-flex align-items-start gap-3">
          <div className="flex-shrink-0 text-success fs-4">
            <i className="feather-check-circle"></i>
          </div>
          <div className="flex-grow-1">
            <h6 className="fw-bold mb-1">
              {t('home.companyGuide.allDoneTitle', 'Allt klart – du är redo!')}
            </h6>
            <p className="text-muted small mb-2">
              {t(
                'home.companyGuide.allDoneBody',
                'Du har genomfört alla startsteg. Skapa och signera dina avtal inuti projekten.'
              )}
            </p>
            <button
              type="button"
              className="btn btn-sm btn-link p-0"
              onClick={() => setForceOpen(true)}
            >
              {t('home.companyGuide.reopen', 'Visa kom igång-guiden igen')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  const renderStepButton = (key: StepKey) => {
    const isNext = key === nextIncomplete;
    const done = steps[key];
    const btnClass = `btn ${isNext ? 'btn-primary' : 'btn-outline-primary'}`;

    if (done) {
      return (
        <span className="badge bg-success-subtle text-success">
          <i className="feather-check me-1"></i>
          {t('home.companyGuide.statusDone', 'Klar')}
        </span>
      );
    }

    let inner: React.ReactNode = null;
    switch (key) {
      case 'customerInvited':
        inner = (
          <button type="button" className={btnClass} onClick={onInviteCustomer}>
            <i className="feather-user-plus me-2"></i>
            {t('home.companyGuide.step1.cta', 'Bjud in beställare')}
          </button>
        );
        break;
      case 'kycApproved':
        inner = kycOnboardingUrl ? (
          <a className={btnClass} href={kycOnboardingUrl} target="_blank" rel="noopener noreferrer">
            <i className="feather-external-link me-2"></i>
            {t('home.companyGuide.step2.cta', 'Starta kundkännedom')}
          </a>
        ) : (
          <button type="button" className={btnClass} onClick={onStartKyc} disabled={isStartingKyc}>
            {isStartingKyc ? (
              <span className="spinner-border spinner-border-sm me-2" role="status"></span>
            ) : (
              <i className="feather-play me-2"></i>
            )}
            {t('home.companyGuide.step2.cta', 'Starta kundkännedom')}
          </button>
        );
        break;
      case 'projectCreated':
        inner = (
          <Link to="/projects/create" className={btnClass}>
            <i className="feather-folder-plus me-2"></i>
            {t('home.companyGuide.step3.cta', 'Skapa projekt')}
          </Link>
        );
        break;
      case 'practicalInfoComplete':
        inner = (
          <Link to="/settings/company?tab=practical" className={btnClass}>
            <i className="feather-edit me-2"></i>
            {t('home.companyGuide.step4.cta', 'Fyll i praktisk information')}
          </Link>
        );
        break;
      default:
        return null;
    }
    // Full-width button on mobile (d-grid stretches the single child), auto
    // width from md and up (d-md-block).
    return <div className="d-grid d-md-block">{inner}</div>;
  };

  const steps1: {
    key: StepKey;
    icon: string;
    title: string;
    help: string;
  }[] = [
    {
      key: 'customerInvited',
      icon: 'user-plus',
      title: t('home.companyGuide.step1.title', 'Bjud in din beställare'),
      help: t(
        'home.companyGuide.step1.help',
        'Bjud in beställaren så att ni kan samarbeta kring avtalet på Etappio.'
      )
    },
    {
      key: 'kycApproved',
      icon: 'shield',
      title: t('home.companyGuide.step2.title', 'Starta kundkännedom (KYC)'),
      help: t(
        'home.companyGuide.step2.help',
        'Genomför kundkännedom hos vår partner Adyen. Krävs för att kunna ta emot utbetalningar.'
      )
    },
    {
      key: 'projectCreated',
      icon: 'folder-plus',
      title: t('home.companyGuide.step3.title', 'Skapa ditt första projekt'),
      help: t(
        'home.companyGuide.step3.help',
        'Skapa ett projekt – det är här du bygger och signerar avtalet.'
      )
    },
    {
      key: 'practicalInfoComplete',
      icon: 'edit',
      title: t('home.companyGuide.step4.title', 'Fyll i företagsuppgifter och praktisk information'),
      help: t(
        'home.companyGuide.step4.help',
        'Komplettera företagsuppgifter och en företagspresentation med information om er verksamhet och era tjänster – så får beställare, jurister och besiktningsmän snabbt en tydlig bild av vilka ni är och bygger förtroende redan från start.'
      )
    }
  ];

  const part2Steps = [
    t('home.companyGuide.part2.milestones', 'Lägg upp etappmål'),
    t('home.companyGuide.part2.createContract', 'Skapa ditt avtal'),
    t('home.companyGuide.part2.preview', 'Förhandsgranska'),
    t('home.companyGuide.part2.sign', 'Signera och skicka med BankID')
  ];

  return (
    <div className="card mb-4">
      <div className="card-header d-flex justify-content-between align-items-center gap-2">
        <h5 className="card-title mb-0 d-flex align-items-center gap-2 flex-grow-1" style={{ minWidth: 0 }}>
          <i className="feather-smile text-primary flex-shrink-0"></i>
          <span className="text-break">
            {t('home.companyGuide.greeting', { name, defaultValue: 'Välkommen till Etappio!' })}
          </span>
        </h5>
        <button
          type="button"
          className="btn btn-sm btn-light-primary flex-shrink-0"
          onClick={() => onToggleCollapsed(!collapsed)}
          aria-expanded={!collapsed}
          aria-label={collapsed ? t('common.expand', 'Visa mer') : t('common.collapse', 'Minimera')}
          title={collapsed ? t('common.expand', 'Visa mer') : t('common.collapse', 'Minimera')}
        >
          <i className={`feather-chevron-${collapsed ? 'down' : 'up'}`}></i>
        </button>
      </div>

      <div
        aria-hidden={collapsed}
        style={{
          maxHeight: collapsed ? 0 : 4000,
          opacity: collapsed ? 0 : 1,
          overflow: 'hidden',
          transition: animate ? 'max-height 0.4s ease, opacity 0.4s ease' : 'none'
        }}
      >
        <div className="card-body">
          <p className="text-muted mb-4">
            {t('home.companyGuide.intro', 'Du är bara några steg från ditt första digitala avtal.')}
          </p>

          {/* Video thumbnail + progress. On mobile the thumbnail sits above the progress. */}
          <div className="d-flex flex-column flex-md-row gap-3 align-items-md-center mb-4">
            {WELCOME_VIDEO_ID && (
              <div style={{ width: '100%', maxWidth: 280 }}>
                {videoPlaying ? (
                  <div className="ratio ratio-16x9 rounded overflow-hidden">
                    <iframe
                      src={`https://www.youtube-nocookie.com/embed/${WELCOME_VIDEO_ID}?autoplay=1&rel=0&modestbranding=1&cc_load_policy=1`}
                      title={t('home.companyGuide.videoIframeTitle', 'Etappio för hantverkare – presentation')}
                      loading="lazy"
                      allow="autoplay; fullscreen"
                      referrerPolicy="strict-origin-when-cross-origin"
                      allowFullScreen
                      style={{ border: 0 }}
                    ></iframe>
                  </div>
                ) : (
                  <button
                    type="button"
                    className="ratio ratio-16x9 rounded overflow-hidden border-0 p-0 position-relative w-100"
                    onClick={() => setVideoPlaying(true)}
                    aria-label={t('home.companyGuide.videoPlayAria', 'Spela upp: Etappio för hantverkare')}
                    style={{ cursor: 'pointer' }}
                  >
                    <img
                      src={`https://i.ytimg.com/vi/${WELCOME_VIDEO_ID}/hqdefault.jpg`}
                      alt={t('home.companyGuide.videoIframeTitle', 'Etappio för hantverkare – presentation')}
                      loading="lazy"
                      style={{ objectFit: 'cover', width: '100%', height: '100%' }}
                    />
                    <span
                      className="position-absolute top-50 start-50 translate-middle d-flex align-items-center justify-content-center"
                      style={{
                        width: 48,
                        height: 48,
                        borderRadius: '50%',
                        backgroundColor: 'rgba(0,0,0,0.6)',
                        color: '#fff'
                      }}
                    >
                      <i className="feather-play"></i>
                    </span>
                  </button>
                )}
                {videoCaption && (
                  <small className="text-muted d-block mt-2">{videoCaption}</small>
                )}
                {isEnglish && (
                  <small className="text-muted d-block mt-1">
                    {t('home.companyGuide.videoInSwedish', 'This video is in Swedish.')}
                  </small>
                )}
              </div>
            )}

            <div className="flex-grow-1">
              <div className="d-flex justify-content-between align-items-center mb-1">
                <span className="fw-semibold small">
                  {t('home.companyGuide.progress', {
                    done: doneCount,
                    total: order.length,
                    defaultValue: '{{done}} av {{total}} klara'
                  })}
                </span>
              </div>
              <div className="progress" style={{ height: 8 }} role="progressbar"
                aria-valuenow={doneCount} aria-valuemin={0} aria-valuemax={order.length}>
                <div
                  className="progress-bar bg-success"
                  style={{ width: `${(doneCount / order.length) * 100}%` }}
                ></div>
              </div>
            </div>
          </div>

          {/* Part 1 — do this now */}
          <h6 className="fw-bold mb-3">{t('home.companyGuide.part1Title', 'Gör detta nu')}</h6>
          <ol className="list-unstyled mb-4">
            {steps1.filter(step => order.includes(step.key)).map((step) => {
              const done = steps[step.key];
              const isNext = step.key === nextIncomplete;
              const kycEmphasis = step.key === 'kycApproved' && !done;
              return (
                <li
                  key={step.key}
                  className={`border rounded p-3 mb-3 ${isNext ? 'border-primary border-2' : ''}`}
                  style={done ? { opacity: 0.75 } : undefined}
                >
                  <div className="d-flex flex-column flex-md-row gap-3 align-items-md-center">
                    <div className={`flex-shrink-0 fs-4 ${done ? 'text-success' : 'text-primary'}`}>
                      <i className={`feather-${done ? 'check-circle' : step.icon}`}></i>
                    </div>
                    <div className="flex-grow-1">
                      <div className="d-flex flex-wrap align-items-center gap-2 mb-1">
                        <span
                          className="fw-bold"
                          style={done ? { textDecoration: 'line-through' } : undefined}
                        >
                          {step.title}
                        </span>
                        {done ? (
                          <span className="badge bg-success-subtle text-success">
                            {t('home.companyGuide.statusDone', 'Klar')}
                          </span>
                        ) : (
                          <span className="badge bg-secondary-subtle text-secondary">
                            {t('home.companyGuide.statusNotStarted', 'Ej påbörjad')}
                          </span>
                        )}
                        {kycEmphasis && (
                          <span className="badge bg-warning text-dark">
                            {t('home.companyGuide.blocksPayments', 'Blockerar insättningar och utbetalningar')}
                          </span>
                        )}
                      </div>
                      <p className="text-muted small mb-0">{step.help}</p>
                    </div>
                    <div className="flex-shrink-0">{renderStepButton(step.key)}</div>
                  </div>
                </li>
              );
            })}
          </ol>

          {showKycStep && kycMessage && (
            <div
              className={`alert alert-${kycMessage.type === 'success' ? 'success' : 'danger'} py-2 mb-4 small`}
            >
              {kycMessage.text}
            </div>
          )}

          {/* Part 2 — how you create the contract (dimmed, no actions) */}
          <div className="border rounded p-3 bg-light">
            <button
              type="button"
              className="btn btn-link p-0 text-decoration-none d-flex align-items-center gap-2 text-muted fw-semibold"
              onClick={() => setPart2Open((open) => !open)}
              aria-expanded={part2Open}
            >
              <i className={`feather-chevron-${part2Open ? 'up' : 'down'}`}></i>
              {t('home.companyGuide.part2Title', 'Så skapar du avtalet')}
            </button>
            {part2Open && (
              <div className="mt-2">
                <p className="text-muted small mb-2">
                  {t(
                    'home.companyGuide.part2Intro',
                    'Nästa steg gör du inuti projektet, när det är skapat.'
                  )}
                </p>
                <ol className="text-muted small mb-0 ps-3">
                  {part2Steps.map((label) => (
                    <li key={label}>{label}</li>
                  ))}
                </ol>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
